import { Flex, ListBox, Pagination, TreeView } from '@eleveo/components-react';
import React, { Key, useCallback, useState } from 'react';
import { useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';

export type TreeViewModel = {
  id: string;
  name: string;
  childItems: TreeViewModel[];
};

export type TeamSelectorTreeViewProps = {
  teamsTree: TreeViewModel[];
  selectedTeams: string[];
  field: string;
  isDisabled: boolean;
} & Pick<React.ComponentPropsWithoutRef<typeof TreeView>, 'disabledKeys' | 'className' | 'testId'>;

/**
 * Right now only supports teams without tree structure
 */
export const TeamSelectorListBox: React.FC<TeamSelectorTreeViewProps> = ({
  teamsTree,
  selectedTeams,
  testId,
  isDisabled,
  className,
  field,
}) => {
  const {
    input: { onChange },
  } = useField(field, { subscription: {} });

  const { t } = useTranslation();
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [selectedPageSize, setSelectedPageSize] = useState<number>(10);

  const submit = useCallback(
    (submittedTeams: Set<Key>) => {
      const newTeams = Array.from(submittedTeams ?? selectedTeams).filter((teamId) => {
        return teamsTree.map((tt) => tt.id).includes(teamId.toString())
      }
      );
      onChange(newTeams);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange]
  );

  const getTeams = (): TreeViewModel[] => {
    const startIndex = ((selectedPage - 1) * selectedPageSize); // 0, 10, 20, 30, ... 
    const endIndex = selectedPage * selectedPageSize; // 10, 20, 30, 40, ...
    return teamsTree.slice(startIndex, endIndex);
  }

  return (
    <Flex className="flex-grow h-auto" column gap={20}>
      <ListBox
        aria-label={t('myConfigurations.teamSelector.name')}
        items={getTeams()}
        onSelectionChange={submit}
        selectionMode="multiple"
        testId={testId}
        className={className}
        selectedKeys={selectedTeams}
        disabledKeys={isDisabled ? teamsTree.map(item => item.id) : []}
      />
      <Pagination
        selectedPage={selectedPage}
        selectedPageSize={selectedPageSize}
        showInfo={false}
        defaultPageSize={10}
        boundaryPages={0}
        siblingPages={0}
        showAllPages={false}
        onPageChange={(page, pageSize) => {
          if (page) {
            setSelectedPage(page);
          }
          if (pageSize) {
            setSelectedPageSize(pageSize);
          }
        }}
        totalItems={teamsTree.length}
      />
    </Flex>
  );
};
